import type { LearningCenterData, Level, Module } from "./types";

// Quiz auto-completion helpers — ported from composable
// `hec/builder/helpers/learningCenterHelpers.js`. They compare the Builder
// content structure (level/module refs authored in Builder) against stored SFCC
// progress to decide whether a level/goal is fully complete.

/** A Builder reference whose target's id is at `.value.data.id` or `.data.id`. */
type BuilderRef = {
  value?: { data?: { id?: string } };
  data?: { id?: string };
};
type BuilderModuleRef = { module: BuilderRef };
type BuilderLevelRef = { level: BuilderRef };

const refId = (ref: BuilderRef | undefined): string | undefined =>
  ref?.value?.data?.id ?? ref?.data?.id;

/**
 * First Builder module in `builderLevel` not yet complete in SFCC progress, or
 * null when all complete. Ported from composable `getNextIncompleteModuleID`
 * (SFCC-11503) — drives the goal-banner "next module" CTA.
 */
export function getNextIncompleteModuleID(
  builderLevel: { modules: BuilderModuleRef[] } | null | undefined,
  sfccLevel: { modules?: Module[] } | null | undefined,
): string | null {
  if (!builderLevel) return null;
  const sfccModules = sfccLevel?.modules ?? [];
  for (const bm of builderLevel.modules) {
    const id = refId(bm.module);
    if (!sfccModules.find(m => m.id === id)?.complete) return id ?? null;
  }
  return null;
}

/** True when every Builder-authored module in the level is complete in SFCC. */
export function areAllModulesComplete(
  builderModules: BuilderModuleRef[],
  sfccModules: Module[],
): boolean {
  return builderModules.every(bm => {
    const id = refId(bm.module);
    return Boolean(sfccModules.find(m => m.id === id)?.complete);
  });
}

/** True when every Builder-authored level in the goal is complete in SFCC. */
export function areAllLevelsComplete(
  builderLevels: BuilderLevelRef[],
  sfccLevels: Level[],
): boolean {
  return builderLevels.every(bl => {
    const id = refId(bl.level);
    return Boolean(sfccLevels.find(l => l.id === id)?.complete);
  });
}

/**
 * Id of the most recently started-or-completed goal that is NOT the active
 * goal, or null when none exists. Ported verbatim from composable
 * `getMostRecentNonActiveGoal` — feeds the HSA `previousHSAPersona` identify
 * trait (LC-AN-03 / SFCC-11509). "Recency" is `max(start, complete)`; a goal
 * with no `start` parses to `NaN` (Invalid Date) and loses every comparison,
 * matching the composable behaviour.
 */
export function getMostRecentNonActiveGoal(
  data: LearningCenterData,
): string | null {
  if (!data?.goals || !Array.isArray(data.goals)) return null;

  const { activeGoal, goals } = data;
  const nonActiveGoals = goals.filter(goal => goal.id !== activeGoal);
  if (nonActiveGoals.length === 0) return null;

  let mostRecentGoal: (typeof nonActiveGoals)[number] | null = null;
  let mostRecentTime = 0;

  for (const goal of nonActiveGoals) {
    const startTime = new Date(goal.start ?? "").getTime();
    const completeTime = goal.complete ? new Date(goal.complete).getTime() : 0;
    const recentTime = Math.max(startTime, completeTime);
    if (!mostRecentGoal || recentTime > mostRecentTime) {
      mostRecentGoal = goal;
      mostRecentTime = recentTime;
    }
  }

  return mostRecentGoal?.id ?? null;
}
